import { useRef, useState, useEffect } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import subscriptionsApi from 'api/subscriptions.api';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const fetchTrialInfo = async () => {
      try {
        const res = await subscriptionsApi.getInfo();
        const { organization, daysRemaining } = res.data;
        const notifs = [];

        if (organization.subscriptionStatus === 'trial' && daysRemaining !== null) {
          if (daysRemaining <= 1) {
            notifs.push({
              id: 'trial-ending-critical',
              icon: <ExclamationCircleOutlined style={{ fontSize: 20, color: '#d32f2f' }} />,
              title: 'Trial Expires Today',
              message: daysRemaining <= 0
                ? 'Your free trial ends today! Subscribe now to keep your access.'
                : 'Your free trial expires tomorrow. Subscribe now to avoid losing access.',
              severity: 'error',
              action: 'Subscribe Now'
            });
          } else if (daysRemaining <= 3) {
            notifs.push({
              id: 'trial-ending-urgent',
              icon: <WarningOutlined style={{ fontSize: 20, color: '#ed6c02' }} />,
              title: 'Trial Ending Soon',
              message: `Your free trial expires in ${daysRemaining} days. Subscribe now to avoid losing access.`,
              severity: 'warning',
              action: 'Subscribe Now'
            });
          } else if (daysRemaining <= 7) {
            notifs.push({
              id: 'trial-ending',
              icon: <ClockCircleOutlined style={{ fontSize: 20, color: '#0288d1' }} />,
              title: 'Trial Reminder',
              message: `You have ${daysRemaining} days left on your free trial. Explore our plans to find the best fit.`,
              severity: 'info',
              action: 'View Plans'
            });
          } else if (daysRemaining <= 14) {
            notifs.push({
              id: 'trial-info',
              icon: <ClockCircleOutlined style={{ fontSize: 20, color: '#0288d1' }} />,
              title: 'Free Trial Active',
              message: `You have ${daysRemaining} days remaining on your free trial.`,
              severity: 'info',
              action: 'View Plans'
            });
          }
        }

        if (organization.subscriptionStatus === 'cancelled' && organization.subscriptionActiveUntil) {
          const daysLeft = Math.max(0, Math.ceil(
            (new Date(organization.subscriptionActiveUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
          ));
          notifs.push({
            id: 'sub-cancelled',
            icon: <WarningOutlined style={{ fontSize: 20, color: '#ed6c02' }} />,
            title: 'Subscription Cancelled',
            message: `Your subscription has been cancelled. Access continues for ${daysLeft} more day${daysLeft === 1 ? '' : 's'}.`,
            severity: 'warning',
            action: 'Resubscribe'
          });
        }

        setNotifications(notifs);
      } catch {
        // silently fail
      }
    };

    fetchTrialInfo();
  }, []);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleAction = () => {
    setOpen(false);
    navigate('/subscription/pricing');
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
        })}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notification-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={notifications.length} color={notifications.some(n => n.severity === 'error') ? 'error' : 'warning'}>
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notifications"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    notifications.length > 0 ? (
                      <Chip label={notifications.length} color="error" size="small" />
                    ) : null
                  }
                >
                  {notifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No new notifications
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ p: 0 }}>
                      {notifications.map((notif) => (
                        <ListItem
                          key={notif.id}
                          sx={{
                            py: 1.5,
                            px: 2.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {notif.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" fontWeight={600}>
                                {notif.title}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                  {notif.message}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color={notif.severity === 'error' ? 'error' : 'primary'}
                                  onClick={handleAction}
                                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                  {notif.action}
                                </Button>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
